import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Move – Understanding Your Body\'s Activity',
    content: [
      '<strong>Learning Goal:</strong> Recognize how physical activity supports health, identify your current movement patterns, and build an active routine that works for you.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Why movement matters for health',
      '• Understanding activity intensity levels (IPAQ-SF)',
      '• The sitting trap and how to avoid it',
      '• Building sustainable movement habits',
      '<br/>',
      '<strong>Discussion:</strong> Your body is made to move. Regular movement isn\'t punishment–it\'s celebration of what your body can do. The goal is finding activities you enjoy that support your energy and health.'
    ]
  },
  {
    title: 'Why Movement Matters',
    content: [
      'Physical activity is essential for your body\'s health and function. When you move regularly:',
      '<br/>',
      '<strong>Cardiovascular Benefits:</strong>',
      '• Improves heart and lung function',
      '• Strengthens blood vessels and circulation',
      '• Reduces risk of heart disease and stroke',
      '<br/>',
      '<strong>Mental Health Benefits:</strong>',
      '• Releases endorphins that boost mood',
      '• Reduces stress and anxiety',
      '• Improves focus and cognitive function',
      '<br/>',
      '<strong>Physical Benefits:</strong>',
      '• Builds strength and muscle mass',
      '• Improves flexibility and balance',
      '• Supports healthy weight management',
      '• Reduces risk of chronic illnesses like diabetes and certain cancers',
      '<br/>',
      '<strong>The Mindset Shift:</strong> Movement isn\'t about punishment or "burning calories." It\'s about honoring your body\'s natural design and celebrating what it can accomplish.'
    ]
  },
  {
    title: 'How Active Are You? (Based on IPAQ-SF)',
    content: [
      'The International Physical Activity Questionnaire Short Form (IPAQ-SF) classifies activities by intensity level. Understanding these categories helps you assess and plan your movement.',
      '<br/>',
          `<div class="module-table">
            <div class="table-row header-row">
              <div class="table-header pillar-cell">ACTIVITY TYPE</div>
              <div class="table-header means-cell">EXAMPLES</div>
              <div class="table-header matters-cell">HEALTH BENEFIT</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Vigorous</div>
              <div class="table-cell means-cell">Running, HIIT, fast cycling, basketball</div>
              <div class="table-cell matters-cell">Strong heart & muscle conditioning</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Moderate</div>
              <div class="table-cell means-cell">Brisk walking, dancing, house chores, cycling</div>
              <div class="table-cell matters-cell">Weight management, better mood</div>
            </div>
            <div class="table-row">
              <div class="table-cell pillar-cell">Light</div>
              <div class="table-cell means-cell">Gentle yoga, stretching, walking slowly</div>
              <div class="table-cell matters-cell">Reduces stiffness, aids recovery</div>
            </div>
          </div>`,
      '<br/>',
      '<strong>Key Insight:</strong> You don\'t need intense workouts to be healthy. Consistency and movement variety matter more than intensity. Even light activities add up to significant health benefits.'
    ]
  },
  {
    title: 'The Sitting Trap',
    content: [
      'Modern life often involves too much sitting, which can negatively impact your health. Prolonged sitting slows metabolism, tightens muscles, and reduces energy levels.',
      '<br/>',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header pillar-cell">SITTING HOURS/DAY</div>
          <div class="table-header means-cell">BODY STATUS</div>
          <div class="table-header matters-cell">RECOMMENDATION</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">&lt;4 hrs</div>
          <div class="table-cell means-cell">🟢 Active</div>
          <div class="table-cell matters-cell">Maintain balance</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">4–8 hrs</div>
          <div class="table-cell means-cell">🟡 Moderate risk</div>
          <div class="table-cell matters-cell">Add stretch breaks</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">&gt;8 hrs</div>
          <div class="table-cell means-cell">🔴 Sedentary</div>
          <div class="table-cell matters-cell">Move hourly</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Mini Practice:</strong> Stand, stretch, or walk for 1–2 minutes every hour. Try the "20-20 Move Rule": Every 20 minutes of sitting → move for 20 seconds.',
      '<br/>',
      '<strong>Why This Matters:</strong> Breaking up sitting time improves circulation, reduces muscle tension, and maintains energy levels throughout the day.'
    ],
    additionalSection: {
      title: 'Practical Activity',
      content: [
        'Take a 10-minute "movement audit" of your day:',
        '• <strong>Track sitting time:</strong> How many hours do you sit today?',
        '• <strong>Identify opportunities:</strong> Where could you add movement?',
        '• <strong>Choose one change:</strong> What\'s one sitting break you can add today?',
        '<br/>',
        '<strong>Reflection:</strong> How does reducing sitting time affect your energy and focus?'
      ]
    }
  },
  {
    title: 'Move More, Your Way',
    content: [
      'The key to sustainable movement is finding activities you enjoy and can maintain. Here are practical ways to incorporate more movement into your daily life:',
      '<br/>',
      '<strong>Daily Movement Ideas:</strong>',
      '• Take stairs instead of elevators',
      '• Walk or bike for short trips',
      '• Do household chores with more energy',
      '• Follow short YouTube workout videos',
      '• Play actively with children or pets',
      '• Park farther from entrances',
      '• Take walking meetings or phone calls',
      '<br/>',
      '<strong>Building Your Routine:</strong>',
      '• <strong>Start Small:</strong> Begin with 5-10 minutes of movement you enjoy',
      '• <strong>Make it Social:</strong> Find a movement buddy or join a group',
      '• <strong>Track Progress:</strong> Use a simple app or calendar to mark active days',
      '• <strong>Be Flexible:</strong> If you miss a day, start fresh the next day',
      '<br/>',
      '<strong>Weekly Goal:</strong> Aim for at least 150 minutes of moderate activity per week, but remember that some movement is better than none.'
    ],
    additionalSection: {
      title: 'Reflection',
      content: [
        'Ask yourself these questions to build your movement awareness:',
        '• How many days a week do I move my body intentionally?',
        '• Which physical activities make me feel good after?',
        '• How can I reduce sitting time daily?',
        '<br/>',
        '<strong>Journal Prompt:</strong> "Today, my body feels energized when I…."',
        '<br/>',
        '<strong>Takeaway:</strong> Movement is personal. Find what works for your body, schedule, and preferences. Small, consistent actions create lasting change.'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 1: Move – Understanding Your Body\'s Activity! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about physical activity and movement patterns.',
        'The quiz will help you review key concepts about building healthy movement habits.'
      ]
    }
  }
];

const PhysicalModule1Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('physicalM1');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/physical-module-1/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/physical-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left physical-panel"
        >
          <div className="module-header">
            <h1>Module 1</h1>
            <p className="module-subtitle">Move – Understanding Your Body's Activity</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Recognize how physical activity supports health, identify your current movement patterns, and build an active routine that works for you.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/physical-module-1/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalModule1Page;


